import { useState, useEffect } from "react";
import Nav from "./Nav";
import SidebarTareas from "./SidebarTareas";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebaseConfig";
import { useNavContext } from "../context/NavContext";
import { useTaskContext } from "../context/TaskContext";
import TaskBoard from "./TaskBoard";
import CreateTaskModal from "./CreateTaskModal";
import CreateListModal from "./CreateListModal";
import TaskDetailModal from "./TaskDetailModal";
import AttachModal from "./AttachModal";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import Breadcrumb from "./Breadcrumb";
import TodasView from "./TodasView";
import { useRef } from "react";
import * as BsIcons from "react-bootstrap-icons";
import TareasHeader from "./TareasHeader";
import { useTranslation } from "react-i18next";

const boxStyle = {
  marginTop: 30,
  border: "1px solid #E6EDFF",
  borderRadius: 6,
  padding: "24px 12px",
  minHeight: 440,
  boxSizing: "border-box",
};

function Tareas() {
  const { user, teamOwnerId } = useAuth();
  const { t } = useTranslation();
  const ownerUid = teamOwnerId || user?.uid;
  const { searchTerm, proximasAVencer, proximosEventos } = useNavContext();
  const { listsGlobal, tasksGlobal, listColors, groupsGlobal, deleteList } =
    useTaskContext();
  const { formatDate } = useFunctionsPrevia();
  const [usersList, setUsersList] = useState([]);
  const [groups, setGroups] = useState([]); // Si lo manejas en TaskContext, ajusta
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isActivePlus, setIsActivePlus] = useState(false);
  const [isActiveDash, setIsActiveDash] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [listToEdit, setListToEdit] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [currentList, setCurrentList] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const optionsRef = useRef(null);
  const isComplete = !!selectedProject?.completo;

  useEffect(() => {
    // Cerrar dropdown si clickean fuera del botón/dropdown
    function handleClickOutside(event) {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    }

    if (showOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOptions]);

  useEffect(() => {
    if (!selectedProject?.id || !selectedProject?.groupId) return;

    const unsub = onSnapshot(
      doc(
        db,
        "gruposTareas",
        selectedProject.groupId,
        "projects",
        selectedProject.id,
      ),
      (snap) => {
        if (snap.exists()) {
          // Reemplazamos el seleccionado con la data ACTUALIZADA
          setSelectedProject((prev) => ({
            id: snap.id,
            groupId: prev?.groupId,
            ...snap.data(),
          }));
        }
      },
    );

    return () => unsub();
  }, [selectedProject?.id, selectedProject?.groupId]);

  useEffect(() => {
    setGroups(groupsGlobal);
  }, [groupsGlobal]);

  useEffect(() => {
    if (!selectedProject && groups.length > 0) {
      const allProjects = groups.reduce((acc, group) => {
        const projects = group.projects || [];
        return acc.concat(
          projects.map((proj) => ({ ...proj, groupId: group.id })),
        );
      }, []);
      if (allProjects.length > 0) {
        allProjects.sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0),
        );
        setSelectedProject(allProjects[0]);
      }
    }
  }, [groups, selectedProject]);

  useEffect(() => {
    if (!ownerUid) return;

    const q = query(
      collection(db, "usuarios"),
      where("ownerId", "==", ownerUid),
    );

    const unsub = onSnapshot(q, (snap) => {
      const arr = snap.docs.map((d) => ({
        id: d.id,
        nombre: d.data().nombre,
        apellido: d.data().apellido,
        email: d.data().email,
        // ya no necesitamos avatarUrl aquí
      }));
      setUsersList(arr);
    });

    return () => unsub();
  }, [ownerUid]);

  // datos del grupo actual
  const currentGroup = selectedProject?.groupId
    ? groups.find((g) => g.id === selectedProject.groupId)
    : null;

  // componente de icono guardado en el proyecto (ej. "FolderFill")
  const GroupIcon = selectedProject?.shape
    ? BsIcons[selectedProject.shape]
    : null;

  // color del grupo (fallback a un azul claro)
  const groupColor = currentGroup?.color || "#7DD3FC";

  const handleAttachmentsUpdated = (updatedAttachments) => {
    setSelectedTask((prev) =>
      prev ? { ...prev, attachments: updatedAttachments } : prev,
    );
  };

  const toggleProjectFlag = async (flag) => {
    if (!selectedProject?.id || !selectedProject?.groupId) return;

    const newValue = !selectedProject?.[flag];
    const ref = doc(
      db,
      "gruposTareas",
      selectedProject.groupId,
      "projects",
      selectedProject.id,
    );

    try {
      // Si está completo, NO permitir tocar programado/urgente
      if (
        selectedProject?.completo &&
        (flag === "programado" || flag === "urgente")
      ) {
        Swal.fire(
          "Atención",
          "Este proyecto está completo. Primero quítalo de Completos.",
          "info",
        );
        return;
      }

      if (flag === "completo" && newValue) {
        // al completar, apaga otras flags
        await updateDoc(ref, {
          completo: true,
          programado: false,
          urgente: false,
        });
        setSelectedProject({ tipo: "todas" }); // salir de la vista
      } else {
        await updateDoc(ref, { [flag]: newValue });
      }

      Swal.fire("Listo", "Se actualizó correctamente", "success");
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo actualizar", "error");
    }
  };

  const handleDeleteProject = async () => {
    if (!selectedProject?.id || !selectedProject?.groupId) return;

    const { isConfirmed } = await Swal.fire({
      title: "¿Seguro que quieres eliminar el proyecto?",
      text: `Se eliminará "${selectedProject.projectName}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      confirmButtonColor: "#e11d48",
    });

    if (!isConfirmed) return;

    try {
      // 1) Elimina en Firestore
      await deleteDoc(
        doc(
          db,
          "gruposTareas",
          selectedProject.groupId,
          "projects",
          selectedProject.id,
        ),
      );

      // 2) Actualiza UI (SidebarTareas) de forma optimista
      setGroups((prev) =>
        prev.map((g) =>
          g.id === selectedProject.groupId
            ? {
                ...g,
                projects: (g.projects || []).filter(
                  (p) => p.id !== selectedProject.id,
                ),
              }
            : g,
        ),
      );

      // 3) Sale de la vista del proyecto
      setSelectedProject({ tipo: "todas" });

      Swal.fire("Eliminado", "El proyecto fue eliminado.", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo eliminar el proyecto", "error");
    }
  };

  const assignedUsers = Array.isArray(selectedProject?.assignedTo)
    ? selectedProject.assignedTo
    : [];

  const tagsArray = Array.isArray(selectedProject?.tags)
    ? selectedProject.tags
    : [];

  const uniqueAssigned = Array.from(
    new Map(assignedUsers.map((u) => [u.id, u])).values(),
  );

  // Zoom
  const handleZoomChange = (val) => {
    const level = Math.max(1, Math.min(4, val));
    setZoomLevel(level);
  };
  const increaseZoom = () => {
    if (zoomLevel < 4) {
      handleZoomChange(zoomLevel + 1);
      setIsActivePlus(true);
      setTimeout(() => setIsActivePlus(false), 200);
    }
  };
  const decreaseZoom = () => {
    if (zoomLevel > 1) {
      handleZoomChange(zoomLevel - 1);
      setIsActiveDash(true);
      setTimeout(() => setIsActiveDash(false), 200);
    }
  };

  // Filtrar listas/tareas por projectId
  const projectLists =
    selectedProject?.tipo === "todas"
      ? listsGlobal.filter((l) => l.projectId == null) // fastLists
      : selectedProject
        ? listsGlobal.filter((l) => l.projectId === selectedProject.id)
        : [];

  /*  >>> AQUÍ ESTABA EL PROBLEMA  <<<  */
  const projectTasks =
    selectedProject?.tipo === "todas"
      ? tasksGlobal.filter((t) => t.projectId == null) // ← incluye las “rápidas”
      : selectedProject
        ? tasksGlobal.filter((t) => t.projectId === selectedProject.id)
        : [];

  const q = searchInput.trim().toLowerCase();

  // Filtro busqueda
  const filteredTasks = q
    ? projectTasks.filter((t) => {
        const title = (t.title || "").toLowerCase();
        const note = (t.note || "").toLowerCase(); // ← aquí
        return title.includes(q) || note.includes(q);
      })
    : projectTasks;

  const handleDeleteFastListClick = async (listName) => {
    await deleteList(listName, null); // null ⇒ fastLists
  };

  const handleToggleFavorito = async (nuevoValor) => {
    const actionText = nuevoValor
      ? "agregar a favoritos"
      : "quitar de favoritos";
    const confirm = await Swal.fire({
      title: "¿Estás seguro?",
      text: `¿Deseas ${actionText} este proyecto?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí",
      cancelButtonText: "No",
    });

    if (confirm.isConfirmed) {
      try {
        // Actualizamos el campo favorito en el documento del proyecto
        await updateDoc(
          doc(
            db,
            "gruposTareas",
            selectedProject.groupId,
            "projects",
            selectedProject.id,
          ),
          { favorito: nuevoValor },
        );
        Swal.fire("Listo", "Se actualizó correctamente", "success");
      } catch (error) {
        console.error(error);
        Swal.fire("Error", "No se pudo actualizar", "error");
      }
    }
  };

  // Manejo List Modal
  const openNewListModal = (obj = null) => {
    setListToEdit(obj); // { name, color } si es edición
    setIsListModalOpen(true);
  };

  const closeNewListModal = () => {
    setIsListModalOpen(false);
    setListToEdit(null);
  };

  const handleDeleteListClick = async (listName) => {
    if (!selectedProject) return;
    await deleteList(listName, selectedProject.id);
  };

  // Manejo Task Modal
  const openTaskModal = (list, task = null) => {
    // list = objeto completo
    setCurrentList(list);
    setTaskToEdit(task);
    setIsTaskModalOpen(true);
  };

  const closeTaskModal = () => {
    setTaskToEdit(null);
    setCurrentList(null);
    setIsTaskModalOpen(false);
  };

  // Manejo Task Detail
  const openDetailModal = (t) => {
    setSelectedTask(t);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setSelectedTask(null);
    setIsDetailModalOpen(false);
  };

  // Manejo Attach
  const openAttachModal = (t) => {
    setSelectedTask(t);
    setIsAttachModalOpen(true);
  };

  const closeAttachModal = () => {
    setSelectedTask(null);
    setIsAttachModalOpen(false);
  };

  const emptyStyle = {
    fontSize: "18px",
    color: "#888",
    textAlign: "center",
    marginTop: "100px",
  };

  const emptyImageStyle = {
    display: "block",
    margin: "20px auto",
    width: "300px",
    opacity: 0.7,
  };

  return (
    <>
      <Nav
        handleSearch={searchTerm}
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <div className="tareas-layout">
        {/* === TARJETA BLANCA QUE CONTIENE EL SIDEBAR DE TAREAS === */}
        <SidebarTareas
          groups={groups}
          setGroups={setGroups}
          usuarios={usersList}
          projectToEdit={selectedProject} // ← agrégalo
          onSelectProject={(p) => setSelectedProject(p)}
        />

        {/* === CONTENIDO PRINCIPAL === */}
        <div className="tareas-content">
          <Breadcrumb
            aliases={{
              tareas: t("breadcrumbs.tasks", { defaultValue: "Tareas" }),
            }}
            lastLabel={selectedProject?.projectName}
          />

          {selectedProject?.tipo === "todas" ? (
            <TodasView
              searchInput={searchInput}
              boxStyle={boxStyle}
              setSearchInput={setSearchInput}
              zoomLevel={zoomLevel}
              increaseZoom={increaseZoom}
              decreaseZoom={decreaseZoom}
              isActivePlus={isActivePlus}
              isActiveDash={isActiveDash}
              lists={projectLists}
              tasks={filteredTasks}
              listColors={listColors}
              openNewListModal={openNewListModal}
              openTaskModal={openTaskModal}
              openDetailModal={openDetailModal}
              openAttachModal={openAttachModal}
              selectedTask={selectedTask}
              isListModalOpen={isListModalOpen}
              isTaskModalOpen={isTaskModalOpen}
              isDetailModalOpen={isDetailModalOpen}
              isAttachModalOpen={isAttachModalOpen}
              closeNewListModal={closeNewListModal}
              closeTaskModal={closeTaskModal}
              closeDetailModal={closeDetailModal}
              closeAttachModal={closeAttachModal}
              handleAttachmentsUpdated={handleAttachmentsUpdated}
              currentList={currentList}
              taskToEdit={taskToEdit}
              listToEdit={listToEdit}
              setListToEdit={setListToEdit}
              selectedProject={selectedProject}
              usuarios={usersList}
              handleDeleteListClick={handleDeleteFastListClick}
            />
          ) : groups.length === 0 ? (
            <>
              <div className="grupo-empty-toolbar">
                <button className="config-btn" title="Configuración">
                  <i className="bi bi-gear" />
                </button>
                <div className="buscar-input">
                  <i className="bi bi-search" />
                  <input
                    type="text"
                    placeholder="Buscar Tareas..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div
                style={{
                  textAlign: "center",
                  marginTop: "30px",
                  border: "1px solid #E6EDFF",
                  borderRadius: "6px",
                  height: "600px",
                }}
              >
                <p style={emptyStyle}>Crea un grupo para continuar.</p>
                <img
                  src="/img/no-data.svg"
                  alt="Sin grupos"
                  style={emptyImageStyle}
                />
              </div>
            </>
          ) : !selectedProject ? null : (
            <>
              <TareasHeader
                selectedProject={selectedProject}
                GroupIcon={GroupIcon}
                groupColor={groupColor}
                uniqueAssigned={uniqueAssigned}
                assignedUsers={assignedUsers}
                tagsArray={tagsArray}
                formatDate={formatDate}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                optionsRef={optionsRef}
                showOptions={showOptions}
                setShowOptions={setShowOptions}
                isComplete={isComplete}
                toggleProjectFlag={toggleProjectFlag}
                handleDeleteProject={handleDeleteProject}
                openNewListModal={openNewListModal}
                handleToggleFavorito={handleToggleFavorito}
                setSelectedProject={setSelectedProject}
              />

              <div style={boxStyle}>
                {projectLists.length === 0 ? (
                  <>
                    <p style={emptyStyle}>
                      Crea una lista para empezar a organizar tus tareas.
                    </p>
                    <img
                      src="/img/no-data.svg"
                      alt="Sin listas"
                      style={emptyImageStyle}
                    />
                  </>
                ) : (
                  <TaskBoard
                    selectedProject={selectedProject}
                    projectLists={projectLists}
                    projectTasks={filteredTasks}
                    listColors={listColors}
                    zoomLevel={zoomLevel}
                    openNewListModal={openNewListModal}
                    handleDeleteListClick={handleDeleteListClick}
                    openTaskModal={openTaskModal}
                    openDetailModal={openDetailModal}
                    openAttachModal={openAttachModal}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modales */}
      {isListModalOpen && (
        <CreateListModal
          isOpen={isListModalOpen}
          onRequestClose={closeNewListModal}
          listToEdit={listToEdit}
          projectId={selectedProject?.id}
          ownerUid={ownerUid}
        />
      )}
      {isTaskModalOpen && (
        <CreateTaskModal
          isOpen={isTaskModalOpen}
          onRequestClose={closeTaskModal}
          taskToEdit={taskToEdit}
          listName={currentList?.name}
          projectId={selectedProject?.id}
          listId={currentList?.id}
          ownerUid={ownerUid}
          usuarios={usersList}
        />
      )}
      {isDetailModalOpen && (
        <TaskDetailModal
          isOpen={isDetailModalOpen}
          onRequestClose={closeDetailModal}
          selectedTask={selectedTask}
        />
      )}
      {isAttachModalOpen && (
        <AttachModal
          isOpen={isAttachModalOpen}
          onRequestClose={closeAttachModal}
          selectedTask={selectedTask}
          onAttachmentsUpdated={handleAttachmentsUpdated}
        />
      )}
    </>
  );
}

export default Tareas;
